import type { WorkflowStepStatus } from '@/api/services';

/** Ticket statuses where the workflow is still actionable (mirrors legacy). */
export const ACTIVE_TICKET_STATUSES = ['opening', 'work-in-progress', 'pending'];

/** Step names driven by the CS-verification form (approve/reject inline). */
export const CSKH_STEP_NAMES = new Set([
  'cskh_verification',
  'cskh_verify_create_service',
  'cskh_confirm_cancel',
  'final_done',
  'cs_confirm_tax_percentage_update',
  'cskh_xac_nhan',
  'cskh_kiem_tra_hoan_tat',
  'technical_deactivate_service',
]);

/** The step that provisions the service — handled in the provider console (not in workspace yet). */
export const TECHNICAL_STEP_NAMES = new Set(['technical_create_service']);

/** Only `cskh_verification` needs the customer-mailer selection in filled_data. */
export const MAILER_STEP_NAME = 'cskh_verification';

/** Human labels for the workflow steps (English — the rebuild convention). */
const STEP_LABELS: Record<string, string> = {
  cskh_verification: 'CS verification',
  cskh_verify_create_service: 'CS verify creation',
  technical_create_service: 'Technical provisioning',
  final_done: 'Completion',
  cskh_confirm_cancel: 'CS confirm cancel',
  technical_deactivate_service: 'Technical deactivate',
  technical_suspend_service: 'Technical suspend',
  cs_confirm_tax_percentage_update: 'CS confirm tax update',
  finance_approve_tax_percentage_update: 'Finance approve tax update',
  cskh_xac_nhan: 'CS confirmation',
  cskh_kiem_tra_hoan_tat: 'CS final check',
};

/** Title-case a raw step_name as a fallback label. */
export function stepLabel(stepName: string): string {
  return STEP_LABELS[stepName] ?? stepName.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Short helper text shown above a step's action form (mirrors legacy). */
const STEP_DESCRIPTIONS: Record<string, string> = {
  cskh_verification:
    'After you confirm, the ticket moves forward and the Technical team is notified to provision the service.',
  cskh_verify_create_service: 'Verify the created service is correct before completing this step.',
  final_done: 'Complete the workflow to finish activating this service.',
  cskh_confirm_cancel: 'Confirm the cancellation request for this service.',
};

export function stepDescription(stepName: string): string | undefined {
  return STEP_DESCRIPTIONS[stepName];
}

export const STATUS_LABEL: Record<WorkflowStepStatus, string> = {
  done: 'Completed',
  doing: 'In progress',
  blocked: 'Blocked',
  todo: 'Pending',
};

/** @ui tone per step status (badge / circle accent). */
export const STATUS_TONE: Record<WorkflowStepStatus, 'success' | 'primary' | 'danger' | 'neutral'> = {
  done: 'success',
  doing: 'primary',
  blocked: 'danger',
  todo: 'neutral',
};
